<template>
  <div class="li-wrap">
    <Loading :isActive="loading" />

    <CAlert v-if="erroStats" color="danger" class="li-alerta">
      <fa-icon icon="exclamation-triangle" />
      {{ erroStats }}
      <CButton size="sm" color="danger" variant="outline" @click="loadStats">
        Tentar novamente
      </CButton>
    </CAlert>

    <!-- (1) Cards compactos de 64px. -->
    <div v-else class="li-kpis">
      <div class="li-kpi">
        <div class="li-kpi__texto">
          <div class="li-kpi__label">Requisições</div>
          <div class="li-kpi__sub">{{ qtdTiposAtivos }} tipos</div>
        </div>
        <div class="li-kpi__valor">{{ stats ? stats.total : '—' }}</div>
      </div>

      <div class="li-kpi">
        <div class="li-kpi__texto">
          <div class="li-kpi__label">Taxa de sucesso</div>
          <div class="li-kpi__sub">{{ subTaxaSucesso }}</div>
        </div>
        <div class="li-kpi__valor li-kpi__valor--ok">{{ taxaSucesso }}</div>
      </div>

      <!-- (3) Card de erros clicável: aplica/remove o filtro status = erro. -->
      <div
        class="li-kpi li-kpi--erro"
        :class="{ 'li-kpi--erro-ativo': filtrandoErros }"
        title="Filtrar somente erros"
        @click="toggleFiltroErros"
      >
        <div class="li-kpi__texto">
          <div class="li-kpi__label li-kpi__label--erro">Erros</div>
          <div class="li-kpi__sub li-kpi__sub--link">
            {{ filtrandoErros ? 'filtro ativo — limpar' : 'Ver somente erros' }}
          </div>
        </div>
        <div class="li-kpi__valor li-kpi__valor--erro">{{ stats ? stats.erros : '—' }}</div>
      </div>

      <div class="li-kpi">
        <div class="li-kpi__texto">
          <div class="li-kpi__label">Tempo médio</div>
          <!-- (4) Contexto: quantas requisições passaram de 10s. -->
          <div class="li-kpi__sub">{{ rotuloLentas }}</div>
        </div>
        <div class="li-kpi__valor">{{ stats ? formatTempo(stats.tempo_medio) : '—' }}</div>
      </div>

      <!-- (5) Gráfico: total em cinza + erros sobrepostos em vermelho. -->
      <div class="li-kpi li-kpi--chart">
        <div class="li-chart__head">
          <span class="li-kpi__label">Erros no tempo</span>
          <span class="li-chart__faixa">{{ faixaGrafico }}</span>
        </div>
        <svg
          v-if="graficoBarras.length"
          class="li-chart"
          width="100%"
          height="34"
          viewBox="0 0 320 34"
          preserveAspectRatio="none"
        >
          <line x1="0" y1="31" x2="320" y2="31" stroke="#eceff2" stroke-width="1" />
          <template v-for="barra in graficoBarras">
            <rect
              :key="barra.chave + '-t'"
              :x="barra.x"
              :y="barra.yTot"
              :width="barra.w"
              :height="barra.hTot"
              rx="2"
              fill="#dfe4ea"
            >
              <title>{{ barra.tip }}</title>
            </rect>
            <rect
              v-if="barra.hErr > 0"
              :key="barra.chave + '-e'"
              :x="barra.x"
              :y="barra.yErr"
              :width="barra.w"
              :height="barra.hErr"
              rx="2"
              fill="#cf4b4b"
            >
              <title>{{ barra.tip }}</title>
            </rect>
          </template>
        </svg>
        <div v-else class="li-chart__vazio">Sem dados no período</div>
      </div>
    </div>

    <CCard>
      <CCardBody>
        <!-- Filtros: busca + Tipo + Status + período, todos na mesma linha.
             Tipo e Status usam o mesmo FiltroDropdown (aplicação por APLICAR
             ou ao dispensar o painel). -->
        <div class="li-filters">
          <div class="li-filters__search">
            <input
              type="text"
              class="form-control"
              placeholder="Buscar por tipo_id, mensagem ou id"
              v-model="term"
            />
          </div>

          <FiltroDropdown
            v-if="tipos.length"
            rotulo="Tipo:"
            :opcoes="opcoesTipos"
            :cabecalho="`${tipos.length} tipos encontrados`"
            :value="tiposSelecionados"
            :mono="true"
            @input="onTiposAplicados"
          />

          <FiltroDropdown
            rotulo="Status:"
            :opcoes="opcoesStatus"
            cabecalho="Situação da integração"
            :value="statusSelecionados"
            :largura="240"
            @input="onStatusAplicado"
          />

          <FiltroPeriodo :value="periodoFiltro" @input="onPeriodoAplicado" />
        </div>

        <div v-if="erroTipos" class="li-tipos-erro">
          <fa-icon icon="exclamation-triangle" />
          {{ erroTipos }}
          <CButton size="sm" color="link" class="li-tipos-erro__acao" @click="loadTipos">
            Tentar novamente
          </CButton>
        </div>

        <!-- Resumo do filtro de tipos aplicado, preservando a cor de cada um. -->
        <div class="li-tipos" v-if="tiposSelecionados.length">
          <span
            v-for="tipo in tiposSelecionados"
            :key="tipo"
            class="li-badge li-badge--resumo"
            :style="corDoTipo(tipo)"
          >
            {{ tipo }}
          </span>
        </div>

        <!-- Master-detail: lista 460px fixos + detalhe fluido -->
        <div class="li-grid">
          <!-- Lista -->
          <div class="li-list">
            <!-- (11) Contagem e ordenação. -->
            <div class="li-list__head">
              <span class="li-list__contagem">{{ rotuloContagem }}</span>
            </div>

            <CAlert v-if="erroLogs" color="danger" class="li-alerta li-alerta--lista">
              <fa-icon icon="exclamation-triangle" />
              {{ erroLogs }}
              <CButton size="sm" color="danger" variant="outline" @click="loadLogs(1)">
                Tentar novamente
              </CButton>
            </CAlert>

            <div v-else class="li-list__scroll">
              <div v-if="!logs.length && !loading" class="li-empty">
                Nenhum log corresponde aos filtros.
              </div>

              <!-- (6) Identificador em primeiro plano, badge+id abaixo, mensagem por último.
                   (8) Erro ganha fundo e borda; selecionado vence o erro. -->
              <div
                v-for="log in logs"
                :key="log.id"
                class="li-row"
                :class="classeLinha(log)"
                @click="selecionaLog(log.id)"
              >
                <div class="li-row__grid">
                  <span class="li-status-dot" :class="classeDot(log)"></span>

                  <div class="li-row__centro">
                    <div class="li-row__ident">{{ identDe(log).valor }}</div>
                    <div class="li-row__tags">
                      <span class="li-badge" :style="corDoTipo(log.tipo)">{{ log.tipo }}</span>
                      <span class="li-row__id">#{{ log.id }}</span>
                    </div>
                    <div class="li-row__sub">{{ sublinhaDe(log) }}</div>
                  </div>

                  <div class="li-row__direita">
                    <div class="li-row__hora">{{ relData(log.datahora_criacao) }}</div>
                    <!-- (9) Tempo fora da curva em âmbar/vermelho. -->
                    <div class="li-tempo" :class="classeTempo(log.tempo_execucao)">
                      {{ log.tempo_execucao == null ? '—' : '⏱ ' + formatTempo(log.tempo_execucao) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- (11) Paginação por acréscimo. -->
              <div v-if="temMais" class="li-list__mais" @click="carregarMais">
                Carregar mais registros
              </div>
            </div>
          </div>

          <!-- Detalhe -->
          <div class="li-detail">
            <Loading :isActive="loadingDetalhe" :isAbsolute="true" />

            <CAlert v-if="erroSelecao" color="danger" class="li-alerta">
              <fa-icon icon="exclamation-triangle" />
              {{ erroSelecao }}
              <CButton size="sm" color="danger" variant="outline" @click="recarregarSelecionado">
                Tentar novamente
              </CButton>
            </CAlert>

            <div v-else-if="!logSelecionado" class="li-empty li-empty--detalhe">
              Selecione um log na lista para ver os detalhes.
            </div>

            <div v-else class="li-card">
              <!-- (13) Cabeçalho com o identificador de negócio em destaque. -->
              <div class="li-detail__head">
                <div class="li-detail__titulo">
                  <div class="li-detail__kicker">{{ identSelecionado.rotulo }}</div>
                  <div class="li-detail__valor">{{ identSelecionado.valor }}</div>
                  <div class="li-detail__tags">
                    <span class="li-pill" :class="ehErro ? 'li-pill--erro' : 'li-pill--ok'">
                      {{ ehErro ? 'Erro' : 'Sucesso' }}
                    </span>
                    <span class="li-badge" :style="corDoTipo(logSelecionado.tipo)">
                      {{ logSelecionado.tipo }}
                    </span>
                    <span class="li-detail__logid">log #{{ logSelecionado.id }}</span>
                  </div>
                </div>

                <div class="li-detail__acoes">
                  <button type="button" class="li-btn li-btn--azul" @click="copiarTudo">
                    ⧉ Copiar tudo
                  </button>
                </div>
              </div>

              <div class="li-detail__corpo">
                <!-- Aviso do pedido ERP só quando ainda não há número gerado. -->
                <div v-if="mostraNotaOrcamento" class="li-banner li-banner--info">
                  O nº do <strong>pedido no ERP</strong> só é gerado após a integração concluir com
                  sucesso. Identifique este registro pelo <strong>nº do orçamento</strong>
                  (campo <code>tipo_id</code> = <strong>{{ logSelecionado.tipo_id }}</strong>).
                </div>

                <!-- (15) Metadados em faixa única. -->
                <div class="li-meta">
                  <div class="li-meta__col li-meta__col--tid">
                    <div class="li-meta__label">tipo_id</div>
                    <div class="li-meta__valor li-meta__valor--mono">
                      {{ logSelecionado.tipo_id || '—' }}
                    </div>
                  </div>
                  <div class="li-meta__col li-meta__col--col">
                    <div class="li-meta__label">colaborador</div>
                    <div class="li-meta__valor">{{ rotuloColaborador }}</div>
                  </div>
                  <div class="li-meta__col li-meta__col--dh">
                    <div class="li-meta__label">data / hora</div>
                    <div class="li-meta__valor">{{ relData(logSelecionado.datahora_criacao) }}</div>
                  </div>
                  <div class="li-meta__col li-meta__col--tempo">
                    <div class="li-meta__label">execução</div>
                    <div class="li-meta__valor li-tempo" :class="classeTempo(logSelecionado.tempo_execucao)">
                      {{ formatTempo(logSelecionado.tempo_execucao) }}
                    </div>
                  </div>
                </div>

                <!-- Mensagem -->
                <div class="li-secao" v-if="logSelecionado.mensagem">
                  <div class="li-secao__titulo">mensagem</div>
                  <div class="li-mensagem">{{ logSelecionado.mensagem }}</div>
                </div>

                <!-- Cadeia da integração: inclui o registro atual, em ordem cronológica. -->
                <div class="li-secao" v-if="cadeia.length > 1">
                  <div class="li-secao__titulo">
                    Cadeia da integração
                    <span class="li-secao__nota">
                      — {{ cadeia.length }} registros com o mesmo tipo_id
                    </span>
                  </div>
                  <div class="li-cadeia">
                    <div
                      v-for="(elo, indice) in cadeia"
                      :key="elo.id"
                      class="li-elo"
                      :class="{ 'li-elo--atual': mesmoId(elo.id, logSelecionado.id) }"
                      @click="abrirElo(elo)"
                    >
                      <span class="li-elo__passo">{{ indice + 1 }}/{{ cadeia.length }}</span>
                      <span class="li-status-dot" :class="classeDot(elo)"></span>
                      <span class="li-badge" :style="corDoTipo(elo.tipo)">{{ elo.tipo }}</span>
                      <span class="li-elo__meta">
                        #{{ elo.id }} · {{ relData(elo.datahora_criacao) }}
                      </span>
                      <span
                        class="li-elo__acao"
                        :class="{ 'li-elo__acao--atual': mesmoId(elo.id, logSelecionado.id) }"
                      >
                        {{ mesmoId(elo.id, logSelecionado.id) ? 'em exibição' : 'abrir →' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Payloads: split 1.45fr / 1fr, cabeçalho fora da caixa. -->
                <div class="li-payloads">
                  <div
                    class="li-payload"
                    v-for="payload in payloads"
                    :key="payload.chave"
                    :class="'li-payload--' + payload.chave"
                  >
                    <div class="li-payload__header">
                      <div class="li-payload__nome">
                        <span class="li-secao__titulo">{{ payload.titulo }}</span>
                        <span class="li-payload__linhas">{{ payload.tamanho }}</span>
                      </div>
                      <div class="li-payload__acoes">
                        <button
                          type="button"
                          class="li-btn li-btn--mini"
                          :disabled="payload.vazio"
                          @click="copiarPayload(payload)"
                        >
                          ⧉ {{ copiado === payload.chave ? 'Copiado!' : 'Copiar' }}
                        </button>
                        <button
                          type="button"
                          class="li-btn li-btn--mini li-btn--azul"
                          :disabled="payload.vazio"
                          @click="abrirTelaCheia(payload)"
                        >
                          ⛶ Expandir
                        </button>
                      </div>
                    </div>
                    <pre
                      class="li-json"
                      :class="{ 'li-json--erro': payload.chave === 'recebidos' && ehErro }"
                    ><span
                      v-for="(token, indice) in payload.tokens"
                      :key="indice"
                      :class="token.classe"
                    >{{ token.texto }}</span></pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Modal tela cheia com busca dentro do JSON. -->
    <modal
      name="li-payload-fullscreen"
      height="86%"
      width="92%"
      :maxWidth="920"
      :adaptive="true"
      :clickToClose="true"
      @closed="onFecharTelaCheia"
    >
      <div class="li-full" v-if="payloadFullscreen">
        <div class="li-full__head">
          <span class="li-full__titulo">{{ payloadFullscreen.tituloCompleto }}</span>
          <div class="li-full__acoes">
            <span class="li-full__busca">
              <fa-icon icon="search" />
              <input
                type="text"
                v-model="buscaFullscreen"
                placeholder="filtrar linhas do JSON…"
              />
              <span class="li-full__contagem">{{ rotuloBuscaFullscreen }}</span>
            </span>
            <button type="button" class="li-btn li-btn--azul" @click="copiarPayload(payloadFullscreen)">
              ⧉ {{ copiado === payloadFullscreen.chave ? 'Copiado!' : 'Copiar' }}
            </button>
            <button
              type="button"
              class="li-full__fechar"
              @click="$modal.hide('li-payload-fullscreen')"
            >
              ✕
            </button>
          </div>
        </div>
        <pre class="li-json li-json--full"><span
          v-for="(token, indice) in tokensFullscreen"
          :key="indice"
          :class="token.classe"
        >{{ token.texto }}</span></pre>
      </div>
    </modal>
  </div>
</template>

<script>
import { get } from '@/helpers/apiRequest';
import { tokensJson } from '@/helpers/jsonTokens';
import FiltroDropdown from './FiltroDropdown.vue';
import FiltroPeriodo from './FiltroPeriodo.vue';

//  Paleta cíclica atribuída por ordem alfabética dos tipos distintos.
// O fundo usa 14% de alpha e o texto a mesma cor escurecida a 68%, para o
// contraste passar de 4.5:1 (antes texto e fundo tinham o mesmo matiz claro).
const PALETA_TIPOS = [
  '#3d7fc4', '#7a5bd0', '#c07a2b', '#2f9e6b', '#c14a6b',
  '#2b8a9e', '#8a6d1b', '#6b6f76', '#b5651d', '#4a8a2c',
];

const COR_TIPO_PADRAO = { backgroundColor: '#eef0f2', color: '#6b6f76' };

// Tokens do JSON colorido (chave, string, booleano/null, número).
const PAYLOAD_VAZIO = [null, undefined, '', 'null', '[]', '{}'];

function hexParaRgba(hex, alpha) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function escurece(hex, fator) {
  const n = parseInt(hex.slice(1), 16);
  const canal = (v) => Math.round(v * fator).toString(16).padStart(2, '0');
  return `#${canal((n >> 16) & 255)}${canal((n >> 8) & 255)}${canal(n & 255)}`;
}

export default {
  name: 'LogsIntegracao',
  components: { FiltroDropdown, FiltroPeriodo },
  data() {
    return {
      loading: false,
      loadingDetalhe: false,
      logs: [],
      paginator: {},
      stats: null,
      tipos: [],
      /*
       * A tela da integração linka para cá com `?tipos=` para o usuário chegar
       * já filtrado no que interessa — sem isso o link abriria a lista inteira
       * e ele teria de reencontrar o tipo entre dezenas.
       */
      tiposSelecionados: String(this.$route.query.tipos || '')
        .split(',')
        .map((tipo) => tipo.trim())
        .filter(Boolean),
      // Multisseleção: ['1'] sucesso, ['0'] erro, vazio ou ambos = todos.
      statusSelecionados: [],
      term: '',
      // Abre em HOJE de propósito: em bases grandes "tudo" varreria a tabela
      // inteira no primeiro carregamento. A paginação é de 12 em 12.
      periodoFiltro: { modo: 'hoje', dataInicio: '', dataFim: '' },
      logSelecionado: null,
      relacionados: [],
      payloadFullscreen: null,
      buscaFullscreen: '',
      copiado: null,
      debounceBusca: null,
      timeoutCopiado: null,
      erroTipos: null,
      erroLogs: null,
      erroStats: null,
      erroSelecao: null,
      paginaAtual: 1,
      idSelecionado: null,
      tokenLogs: 0,
      tokenStats: 0,
      tokenDetalhe: 0,
    };
  },
  computed: {
    // ---- cores por tipo  ----
    mapaCores() {
      const nomes = [...new Set(this.tipos.map((item) => item.tipo))].sort();
      const mapa = {};

      nomes.forEach((nome, indice) => {
        const base = PALETA_TIPOS[indice % PALETA_TIPOS.length];
        mapa[nome] = {
          backgroundColor: hexParaRgba(base, 0.14),
          color: escurece(base, 0.68),
        };
      });

      return mapa;
    },
    // Opções dos dois dropdowns de filtro.
    opcoesTipos() {
      return [...this.tipos]
        .sort((a, b) => String(a.tipo).localeCompare(String(b.tipo), 'pt-BR'))
        .map((item) => ({
          valor: item.tipo,
          rotulo: item.tipo,
          total: item.total,
          cor: this.corDoTipo(item.tipo).color,
        }));
    },
    // Sem contagem de propósito: os totais de stats refletem o filtro corrente,
    // então mostrariam "Sucesso 0" justamente ao filtrar apenas erros.
    opcoesStatus() {
      return [
        { valor: '1', rotulo: 'Sucesso', cor: '#62a744' },
        { valor: '0', rotulo: 'Erro', cor: '#cf4b4b' },
      ];
    },

    // ---- KPIs ----
    taxaSucesso() {
      if (!this.stats) {
        return '—';
      }

      const total = Number(this.stats.total);
      if (!isFinite(total) || total <= 0) {
        return '—';
      }

      const sucessos = this.qtdSucessos;
      return isFinite(sucessos) ? `${Math.round((sucessos / total) * 100)}%` : '—';
    },
    qtdSucessos() {
      if (!this.stats) {
        return NaN;
      }

      return this.stats.sucessos != null
        ? Number(this.stats.sucessos)
        : Number(this.stats.total) - (Number(this.stats.erros) || 0);
    },
    subTaxaSucesso() {
      if (!this.stats || !isFinite(this.qtdSucessos)) {
        return '—';
      }

      return `${this.qtdSucessos} de ${this.stats.total}`;
    },
    qtdTiposAtivos() {
      return this.tiposSelecionados.length || this.tipos.length;
    },
    filtrandoErros() {
      return this.statusSelecionados.length === 1 && this.statusSelecionados[0] === '0';
    },
    rotuloLentas() {
      if (!this.stats || this.stats.lentas == null) {
        return '';
      }

      const lentas = Number(this.stats.lentas);
      return lentas ? `${lentas} acima de 10s` : 'nenhuma acima de 10s';
    },

    // ---- gráfico (5) ----
    graficoBarras() {
      const serie = (this.stats && this.stats.serie) || [];
      if (!serie.length) {
        return [];
      }

      const buckets = serie.slice(-12);
      const maiorTotal = Math.max(1, ...buckets.map((b) => Number(b.total) || 0));
      const largura = 320 / 12;
      const base = 31;
      const altura = 27;

      return buckets.map((bucket, indice) => {
        const total = Number(bucket.total) || 0;
        const erros = Number(bucket.erros) || 0;
        // Arredonda o topo e deriva a altura dele, para a barra assentar exatamente
        // na linha de base (arredondar os dois em separado deslocava ~0.1px).
        const yTot = Number((base - (total / maiorTotal) * altura).toFixed(1));
        // Mínimo de 2.5px para 1 erro não desaparecer ao lado de um bucket cheio.
        const alturaErro = erros ? Math.max(2.5, (erros / maiorTotal) * altura) : 0;
        const yErr = Number((base - alturaErro).toFixed(1));

        return {
          chave: String(bucket.periodo) + indice,
          x: (indice * largura + 2.5).toFixed(1),
          w: (largura - 5).toFixed(1),
          yTot,
          hTot: Number((base - yTot).toFixed(1)),
          yErr,
          hErr: Number((base - yErr).toFixed(1)),
          tip: `${this.rotuloBucket(bucket.periodo)} · ${total} req · ${erros} erro(s)`,
        };
      });
    },
    faixaGrafico() {
      const barras = (this.stats && this.stats.serie) || [];
      if (!barras.length) {
        return '—';
      }

      const buckets = barras.slice(-12);
      const inicio = this.rotuloBucket(buckets[0].periodo);
      const fim = this.rotuloBucket(buckets[buckets.length - 1].periodo);

      return inicio === fim ? inicio : `${inicio}–${fim}`;
    },

    // ---- lista ----
    rotuloContagem() {
      const total = this.paginator.total != null ? this.paginator.total : this.logs.length;
      return `exibindo ${this.logs.length} de ${total} registros`;
    },
    temMais() {
      const { current_page: atual, last_page: ultima } = this.paginator;
      return !!(atual && ultima && atual < ultima);
    },

    // ---- detalhe ----
    ehErro() {
      return !!this.logSelecionado && !+this.logSelecionado.sucesso;
    },
    recebidosSelecionado() {
      return this.parseJson(this.logSelecionado && this.logSelecionado.dados_recebidos);
    },
    identSelecionado() {
      return this.identDe(this.logSelecionado, this.recebidosSelecionado);
    },
    // O aviso só aparece quando ainda não existe número de pedido no ERP.
    mostraNotaOrcamento() {
      if (!this.logSelecionado || !this.logSelecionado.tipo_id) {
        return false;
      }

      if (!/^PEDIDO_ENVIADO/.test(String(this.logSelecionado.tipo).toUpperCase())) {
        return false;
      }

      return !this.numeroPedidoErp(this.recebidosSelecionado);
    },
    // Nunca mostra o id cru sozinho.
    rotuloColaborador() {
      const log = this.logSelecionado || {};
      const id = log.colaborador_id;

      if (id == null || id === '') {
        return '— (sistema)';
      }

      const nome = log.colaborador_nome || `Colaborador ${id}`;
      return `${nome} (#${id})`;
    },
    // Cadeia completa em ordem cronológica crescente.
    cadeia() {
      if (!this.logSelecionado || !this.logSelecionado.tipo_id) {
        return [];
      }

      return [this.logSelecionado, ...this.relacionados].slice().sort((a, b) => {
        const da = String(a.datahora_criacao || '');
        const db = String(b.datahora_criacao || '');

        if (da === db) {
          return Number(a.id) - Number(b.id);
        }

        return da < db ? -1 : 1;
      });
    },
    payloads() {
      return [
        this.montaPayload(
          'enviados',
          'dados_enviados',
          this.logSelecionado && this.logSelecionado.dados_enviados
        ),
        this.montaPayload(
          'recebidos',
          'dados_recebidos',
          this.logSelecionado && this.logSelecionado.dados_recebidos
        ),
      ];
    },

    // ---- modal (18) ----
    tokensFullscreen() {
      if (!this.payloadFullscreen) {
        return [];
      }

      return this.tokensJson(this.payloadFullscreen.conteudo, this.buscaFullscreen);
    },
    rotuloBuscaFullscreen() {
      if (!this.payloadFullscreen || !this.buscaFullscreen.trim()) {
        return '';
      }

      const termo = this.buscaFullscreen.trim().toLowerCase();
      const linhas = String(this.payloadFullscreen.conteudo)
        .split('\n')
        .filter((linha) => linha.toLowerCase().includes(termo));

      return `${linhas.length} linhas`;
    },
  },
  watch: {
    term() {
      clearTimeout(this.debounceBusca);
      this.debounceBusca = setTimeout(() => this.aplicarFiltros(), 400);
    },
  },
  methods: {
    // ---- cores / formatação -----------------------------------------------
    corDoTipo(tipo) {
      return this.mapaCores[tipo] || COR_TIPO_PADRAO;
    },
    mesmoId(a, b) {
      return a != null && b != null && String(a) === String(b);
    },
    ehSucesso(log) {
      return !!+(log && log.sucesso);
    },
    classeDot(log) {
      return this.ehSucesso(log) ? 'li-status-dot--sucesso' : 'li-status-dot--erro';
    },
    classeLinha(log) {
      const selecionada = this.logSelecionado && this.mesmoId(this.logSelecionado.id, log.id);

      return {
        'li-row--ativo': !!selecionada,
        'li-row--erro': !selecionada && !this.ehSucesso(log),
      };
    },
    // Faixas de tempo: < 10s neutro, >= 10s âmbar, >= 30s vermelho.
    classeTempo(tempo) {
      const valor = parseFloat(tempo);

      if (tempo == null || isNaN(valor)) {
        return 'li-tempo--nulo';
      }
      if (valor >= 30) {
        return 'li-tempo--critico';
      }
      if (valor >= 10) {
        return 'li-tempo--lento';
      }

      return 'li-tempo--normal';
    },
    formatTempo(tempo) {
      const valor = parseFloat(tempo);

      if (tempo == null || isNaN(valor)) {
        return '—';
      }

      return `${valor.toFixed(2)}s`;
    },
    // Datas relativas.
    relData(valor) {
      if (!valor) {
        return '—';
      }

      const data = new Date(String(valor).replace(' ', 'T'));
      if (isNaN(data.getTime())) {
        return String(valor);
      }

      const hh = String(data.getHours()).padStart(2, '0');
      const mm = String(data.getMinutes()).padStart(2, '0');
      const hora = `${hh}:${mm}`;

      const soData = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const dias = Math.round((soData(new Date()) - soData(data)) / 86400000);

      if (dias === 0) {
        return `hoje ${hora}`;
      }
      if (dias === 1) {
        return `ontem ${hora}`;
      }

      const dd = String(data.getDate()).padStart(2, '0');
      const mes = String(data.getMonth() + 1).padStart(2, '0');

      return `${dd}/${mes} ${hora}`;
    },
    rotuloBucket(periodo) {
      const data = new Date(String(periodo).replace(' ', 'T'));

      if (isNaN(data.getTime())) {
        return String(periodo);
      }

      if (this.stats && this.stats.bucket === 'hour') {
        return `${String(data.getHours()).padStart(2, '0')}:00`;
      }

      return `${String(data.getDate()).padStart(2, '0')}/${String(data.getMonth() + 1).padStart(2, '0')}`;
    },

    // Rótulo e valor do identificador de negócio, por tipo.
    identDe(log, recebidosJa) {
      if (!log) {
        return { rotulo: 'Identificador', valor: '—' };
      }

      const tipo = String(log.tipo || '').toUpperCase();

      if (!log.tipo_id) {
        const recebidos =
          recebidosJa !== undefined
            ? recebidosJa
            : this.parseJson(log.dados_recebidos || log.dados_recebidos_resumo);

        if (tipo === 'CONSULTA_PRECO_DESLOGADO') {
          const termo =
            (recebidos && !Array.isArray(recebidos) && recebidos.filtros && recebidos.filtros.termo) ||
            // O resumo da listagem vem truncado: o regex tolera o JSON incompleto.
            this.termoNoTexto(log.dados_recebidos_resumo);

          if (termo) {
            return { rotulo: 'Termo pesquisado', valor: termo };
          }
        }

        return { rotulo: 'Identificador', valor: 'sem tipo_id' };
      }

      if (/^PEDIDO_ENVIADO/.test(tipo)) {
        return { rotulo: 'Orçamento', valor: log.tipo_id };
      }
      if (tipo === 'CLIENTE_ENVIADO' || tipo === 'CADASTRO_REVENDEDOR') {
        return { rotulo: 'Documento (CNPJ)', valor: log.tipo_id };
      }
      if (tipo === 'NFE_EMITIDA') {
        return { rotulo: 'Pedido no ERP', valor: log.tipo_id };
      }
      if (tipo === 'ATUALIZACAO_ESTOQUE') {
        return { rotulo: 'Lote', valor: log.tipo_id };
      }
      if (tipo === 'TRANSACAO_CARTAO') {
        return { rotulo: 'Transação', valor: log.tipo_id };
      }

      return { rotulo: 'tipo_id', valor: log.tipo_id };
    },
    termoNoTexto(texto) {
      const encontrado = String(texto || '').match(/"termo"\s*:\s*"((?:[^"\\]|\\.)*)"/i);
      return encontrado ? encontrado[1] : '';
    },
    sublinhaDe(log) {
      if (log.mensagem && String(log.mensagem).trim()) {
        return log.mensagem;
      }

      const id = log.colaborador_id;
      const quem = id == null || id === '' ? '— (sistema)' : `Colaborador ${id} (#${id})`;

      return `${quem} · ${this.relData(log.datahora_criacao)}`;
    },
    numeroPedidoErp(recebidos) {
      if (!recebidos || Array.isArray(recebidos)) {
        return null;
      }

      if (recebidos.pedidoerp_id) {
        return recebidos.pedidoerp_id;
      }

      const pedidos = recebidos.pedidos;
      if (Array.isArray(pedidos) && pedidos[0] && pedidos[0].pedidoerp_id) {
        return pedidos[0].pedidoerp_id;
      }

      return null;
    },

    // ---- JSON colorido (17/18) --------------------------------------------
    parseJson(bruto) {
      if (!bruto || typeof bruto !== 'string') {
        return bruto && typeof bruto === 'object' ? bruto : null;
      }

      try {
        return JSON.parse(bruto);
      } catch (e) {
        return null;
      }
    },
    // O realce vive em @/helpers/jsonTokens: a tela da integração mostra os
    // mesmos payloads e precisava da mesma função.
    tokensJson(texto, filtro) {
      return tokensJson(texto, filtro);
    },
    montaPayload(chave, titulo, bruto) {
      const vazio = PAYLOAD_VAZIO.includes(bruto);
      const analisado = vazio ? null : this.parseJson(bruto);
      const conteudo = vazio
        ? 'NULL — sem retorno'
        : analisado !== null
          ? JSON.stringify(analisado, null, 2)
          : String(bruto);
      const linhas = conteudo.split('\n').length;
      const log = this.logSelecionado || {};

      return {
        chave,
        titulo,
        conteudo,
        vazio,
        tamanho: vazio ? 'vazio' : `${linhas} ${linhas === 1 ? 'linha' : 'linhas'}`,
        tokens: vazio
          ? [{ texto: 'NULL — sem retorno', classe: 'j-nulo' }]
          : this.tokensJson(conteudo),
        tituloCompleto: `${titulo} · ${log.tipo || ''} · #${log.id || ''}`,
      };
    },

    // ---- requisições -------------------------------------------------------
    filtrosQuery() {
      const params = [];

      if (this.term) {
        params.push(`q=${encodeURIComponent(this.term)}`);
      }
      if (this.tiposSelecionados.length) {
        params.push(`tipos=${encodeURIComponent(this.tiposSelecionados.join(','))}`);
      }
      // Sucesso e Erro marcados juntos equivalem a "todos": o parâmetro é
      // omitido, preservando o contrato de valor único do endpoint (status=1|0).
      if (this.statusSelecionados.length === 1) {
        params.push(`status=${this.statusSelecionados[0]}`);
      }
      // Range específico e atalho são mutuamente exclusivos (na API o range
      // tem precedência de qualquer forma).
      const { modo, dataInicio, dataFim } = this.periodoFiltro;

      if (modo === 'custom') {
        if (dataInicio) {
          params.push(`data_inicio=${encodeURIComponent(dataInicio)}`);
        }
        if (dataFim) {
          params.push(`data_fim=${encodeURIComponent(dataFim)}`);
        }
      } else if (modo !== 'tudo') {
        params.push(`periodo=${modo}`);
      }

      return params.join('&');
    },
    aplicarFiltros() {
      // Qualquer mudança de filtro volta para a primeira página de 12.
      this.loadLogs(1);
      this.loadStats();
    },
    loadTipos() {
      this.erroTipos = null;
      get('/admin/logs/integracao/tipos')
        .then((json) => {
          this.tipos = json.tipos || [];
        })
        .catch(() => {
          this.tipos = [];
          this.erroTipos = 'Não foi possível carregar os tipos de integração.';
        });
    },
    loadLogs(page = 1, acumular = false) {
      this.loading = true;
      this.erroLogs = null;
      this.paginaAtual = page;
      const token = ++this.tokenLogs;

      get(`/admin/logs/integracao?page=${page}&take=12&${this.filtrosQuery()}`)
        .then((json) => {
          if (token !== this.tokenLogs) {
            return;
          }

          const itens = json.itens || [];
          this.logs = acumular ? [...this.logs, ...itens] : itens;
          this.paginator = json.paginator || {};
          this.loading = false;

          const selecionadoNaLista =
            this.logSelecionado &&
            this.logs.some((log) => this.mesmoId(log.id, this.logSelecionado.id));

          if (!selecionadoNaLista && this.logs.length) {
            this.selecionaLog(this.logs[0].id);
          }

          if (!this.logs.length) {
            this.logSelecionado = null;
            this.relacionados = [];
            this.idSelecionado = null;
            this.erroSelecao = null;
          }
        })
        .catch(() => {
          if (token !== this.tokenLogs) {
            return;
          }

          this.loading = false;

          // Ao acumular, preserva o que já está na tela: só a página nova falhou.
          if (!acumular) {
            this.logs = [];
            this.paginator = {};
          }

          this.erroLogs = 'Não foi possível carregar os logs de integração.';
        });
    },
    carregarMais() {
      this.loadLogs(this.paginaAtual + 1, true);
    },
    loadStats() {
      this.erroStats = null;
      const token = ++this.tokenStats;

      get(`/admin/logs/integracao/stats?${this.filtrosQuery()}`)
        .then((json) => {
          if (token !== this.tokenStats) {
            return;
          }

          this.stats = json;
        })
        .catch(() => {
          if (token !== this.tokenStats) {
            return;
          }

          this.stats = null;
          this.erroStats = 'Não foi possível carregar os indicadores do período.';
        });
    },
    selecionaLog(id) {
      this.loadingDetalhe = true;
      this.erroSelecao = null;
      this.idSelecionado = id;
      const token = ++this.tokenDetalhe;

      get(`/admin/logs/integracao/${id}`)
        .then((json) => {
          if (token !== this.tokenDetalhe) {
            return;
          }

          this.logSelecionado = json.log;
          this.relacionados = json.relacionados || [];
          this.loadingDetalhe = false;
        })
        .catch(() => {
          if (token !== this.tokenDetalhe) {
            return;
          }

          this.loadingDetalhe = false;
          this.logSelecionado = null;
          this.relacionados = [];
          this.erroSelecao = `Não foi possível carregar os detalhes do log #${id}.`;
        });
    },
    recarregarSelecionado() {
      if (this.idSelecionado != null) {
        this.selecionaLog(this.idSelecionado);
      }
    },
    abrirElo(elo) {
      if (!this.mesmoId(elo.id, this.logSelecionado.id)) {
        this.selecionaLog(elo.id);
      }
    },
    // Card de erros aplica/remove o filtro de status.
    toggleFiltroErros() {
      this.statusSelecionados = this.filtrandoErros ? [] : ['0'];
      this.aplicarFiltros();
    },

    // ---- filtros em dropdown (só chegam aqui já aplicados) -----------------
    onTiposAplicados(valores) {
      this.tiposSelecionados = valores;
      this.aplicarFiltros();
    },
    onStatusAplicado(valores) {
      this.statusSelecionados = valores;
      this.aplicarFiltros();
    },
    onPeriodoAplicado(estado) {
      this.periodoFiltro = estado;
      this.aplicarFiltros();
    },

    // ---- copiar / tela cheia ----------------------------------------------
    marcaCopiado(chave) {
      this.copiado = chave;
      clearTimeout(this.timeoutCopiado);
      this.timeoutCopiado = setTimeout(() => {
        this.copiado = null;
      }, 1500);
    },
    copiarTexto(texto, chave) {
      const aviso = () => this.marcaCopiado(chave);

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(texto)
          .then(aviso)
          .catch(() => {
            this.$swal({
              toast: true,
              position: 'top-end',
              icon: 'warning',
              title: 'Não foi possível copiar',
              text: 'Copie o conteúdo manualmente.',
              showConfirmButton: false,
              timer: 3000,
            });
          });
        return;
      }

      const area = document.createElement('textarea');
      area.value = texto;
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      document.body.removeChild(area);
      aviso();
    },
    copiarPayload(payload) {
      this.copiarTexto(payload.conteudo, payload.chave);
    },
    // Copiar tudo: cabeçalho + mensagem + os dois payloads.
    copiarTudo() {
      const log = this.logSelecionado || {};
      const [enviados, recebidos] = this.payloads;

      const texto = [
        `${log.tipo} · log #${log.id} · tipo_id ${log.tipo_id || '—'}`,
        log.datahora_criacao,
        '',
        'MENSAGEM:',
        log.mensagem || '—',
        '',
        'ENVIADOS:',
        enviados.conteudo,
        '',
        'RECEBIDOS:',
        recebidos.conteudo,
      ].join('\n');

      this.copiarTexto(texto, 'tudo');
    },
    abrirTelaCheia(payload) {
      this.buscaFullscreen = '';
      this.payloadFullscreen = payload;
      this.$modal.show('li-payload-fullscreen');
    },
    // Fechar limpa a busca.
    onFecharTelaCheia() {
      this.buscaFullscreen = '';
      this.payloadFullscreen = null;
    },
  },
  beforeMount() {
    this.loadTipos();
    this.loadLogs(1);
    this.loadStats();
  },
  beforeDestroy() {
    clearTimeout(this.debounceBusca);
    clearTimeout(this.timeoutCopiado);
  },
};
</script>

<style scoped>
/* ---------------------------------------------------------------------------
   Tokens do handoff (escopo da aba de Logs de Integração)
   texto #2c3441 · secundário #5a636e · apagado #9aa2ac · muito apagado #b6bcc4
   bordas #e3e6ea / #eef1f4 / #f0f2f5 · marinho #2b3556 · azul #3d7fc4
   sucesso #4a8a2c · erro #cf4b4b · lento #b5651d
--------------------------------------------------------------------------- */

/* ---- Faixa de KPIs ---- */
.li-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr)) 1.6fr;
  gap: 12px;
  margin-bottom: 14px;
}

.li-kpi {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 64px;
  padding: 11px 14px;
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  min-width: 0;
}

.li-kpi__texto {
  min-width: 0;
}

.li-kpi__label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a929c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.li-kpi__label--erro {
  color: #c14a4a;
}

.li-kpi__sub {
  margin-top: 2px;
  font-size: 11px;
  color: #9aa2ac;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.li-kpi__sub--link {
  color: #c98b8b;
  text-decoration: underline;
}

.li-kpi__valor {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  color: #2c3441;
}

.li-kpi__valor--ok {
  color: #4a8a2c;
}

.li-kpi__valor--erro {
  color: #cf4b4b;
}

/* (3) Card de erros clicável */
.li-kpi--erro {
  cursor: pointer;
  border: 1px solid #f2c9c9;
}

.li-kpi--erro-ativo {
  background: #fdf3f3;
  border: 1.5px solid #cf4b4b;
}

/* (5) Gráfico */
.li-kpi--chart {
  display: block;
  padding: 8px 14px 6px;
}

.li-chart__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.li-chart__faixa {
  font-size: 10px;
  color: #9aa2ac;
  white-space: nowrap;
}

.li-chart {
  display: block;
  margin-top: 4px;
}

.li-chart__vazio {
  margin-top: 8px;
  font-size: 11px;
  color: #9aa2ac;
}

/* ---- Alertas ---- */
.li-alerta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.li-alerta .btn {
  margin-left: auto;
  white-space: nowrap;
}

.li-alerta--lista {
  margin: 12px;
}

.li-tipos-erro {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  font-size: 12px;
  color: #cf4b4b;
}

.li-tipos-erro__acao {
  padding: 0;
  font-size: 12px;
}

/* ---- Filtros ---- */
.li-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
}

/* Busca compacta: não precisa esticar, os dropdowns dividem a linha. */
.li-filters__search {
  flex: 1;
  min-width: 180px;
}

/* ---- Badge de tipo (12) ---- */
.li-badge {
  display: inline-block;
  padding: 2px 7px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 9.5px;
  font-weight: 700;
  border-radius: 5px;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.li-badge--resumo {
  font-size: 10.5px;
  padding: 3px 9px;
}

/* ---- Grid mestre-detalhe ---- */
.li-grid {
  display: grid;
  grid-template-columns: 460px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

/* ---- Lista ---- */
.li-list {
  min-width: 0;
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  overflow: hidden;
}

.li-list__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  background: #f7f9fb;
  border-bottom: 1px solid #e3e6ea;
}

.li-list__contagem {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.li-list__scroll {
  max-height: 640px;
  overflow-y: auto;
}

/* (6)(8) Linha */
.li-row {
  padding: 11px 16px;
  border-bottom: 1px solid #f0f2f5;
  border-left: 3px solid transparent;
  background: #fff;
  cursor: pointer;
  min-width: 0;
}

.li-row--erro {
  background: #fdf5f5;
  border-left-color: #cf4b4b;
}

.li-row--ativo {
  background: #f2f7fc;
  border-left-color: #2b3556;
}

.li-row__grid {
  display: grid;
  grid-template-columns: 20px 1fr auto;
  gap: 10px;
  align-items: center;
}

.li-row__centro {
  min-width: 0;
}

.li-row__ident {
  font-size: 13.5px;
  font-weight: 700;
  color: #2c3441;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.li-row__tags {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 4px;
  min-width: 0;
}

.li-row__id {
  font-size: 10px;
  color: #b6bcc4;
  flex-shrink: 0;
}

.li-row__sub {
  margin-top: 4px;
  font-size: 11px;
  color: #9aa2ac;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.li-row__direita {
  text-align: right;
  white-space: nowrap;
  align-self: start;
}

.li-row__hora {
  font-size: 11px;
  color: #7a828e;
}

.li-list__mais {
  padding: 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #3d7fc4;
  background: #fbfcfd;
  border-top: 1px solid #f0f2f5;
  cursor: pointer;
}

.li-list__mais:hover {
  background: #f2f7fc;
}

/* (9) Tempo por faixa */
.li-tempo {
  margin-top: 3px;
  font-size: 11px;
}

.li-tempo--normal {
  color: #9aa2ac;
}

.li-tempo--lento {
  font-weight: 700;
  color: #b5651d;
}

.li-tempo--critico {
  font-weight: 700;
  color: #cf4b4b;
}

.li-tempo--nulo {
  color: #c3cad2;
}

.li-status-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.li-status-dot--sucesso {
  background: #62a744;
}

.li-status-dot--erro {
  background: #cf4b4b;
}

/* ---- Detalhe ---- */
.li-detail {
  min-width: 0;
  position: sticky;
  top: 12px;
}

.li-card {
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  overflow: hidden;
}

/* (13) Cabeçalho */
.li-detail__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 18px;
  border-bottom: 1px solid #eef1f4;
}

.li-detail__titulo {
  min-width: 0;
}

.li-detail__kicker {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.li-detail__valor {
  margin-top: 2px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #2c3441;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.li-detail__tags {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 9px;
}

.li-detail__logid {
  font-size: 11px;
  color: #b6bcc4;
}

.li-detail__acoes {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.li-pill {
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 11px;
}

.li-pill--ok {
  background: #eef6e9;
  color: #4a8a2c;
}

.li-pill--erro {
  background: #fbecec;
  color: #c14a4a;
}

.li-btn {
  padding: 8px 13px;
  font-size: 12px;
  font-weight: 600;
  color: #5a636e;
  background: #fff;
  border: 1px solid #d4d9df;
  border-radius: 7px;
  cursor: pointer;
  white-space: nowrap;
}

.li-btn--azul {
  color: #3d7fc4;
  background: #eef4fb;
  border-color: #cfe0f2;
}

.li-btn--mini {
  padding: 4px 9px;
  font-size: 10.5px;
  border-radius: 6px;
}

.li-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.li-detail__corpo {
  max-height: 600px;
  overflow-y: auto;
}

/* Banner (14) — a falha em si não ganha banner: o texto do erro é o mesmo do
   campo "mensagem", que já aparece logo abaixo. */
.li-banner {
  margin: 14px 18px 0;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.li-banner--info {
  background: #eef4fb;
  border: 1px solid #cfe0f2;
  color: #3a5a80;
}

.li-banner--info code {
  padding: 1px 5px;
  background: #dce8f5;
  border-radius: 3px;
  font-family: ui-monospace, Menlo, monospace;
  color: #3a5a80;
}

/* (15) Faixa de metadados */
.li-meta {
  display: flex;
  align-items: stretch;
  margin: 14px 18px 0;
  background: #fafbfc;
  border: 1px solid #eef1f4;
  border-radius: 8px;
}

.li-meta__col {
  min-width: 0;
  padding: 9px 14px;
}

.li-meta__col + .li-meta__col {
  border-left: 1px solid #eef1f4;
}

.li-meta__col--tid {
  flex: 1.3;
}

.li-meta__col--col {
  flex: 1.2;
}

.li-meta__col--dh {
  flex: 1;
}

.li-meta__col--tempo {
  flex: 0.8;
}

.li-meta__label {
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.li-meta__valor {
  margin-top: 2px;
  font-size: 12.5px;
  color: #2c3441;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.li-meta__valor--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* Seções */
.li-secao {
  padding: 14px 18px 0;
  min-width: 0;
}

.li-secao__titulo {
  margin-bottom: 5px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.li-secao__nota {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: #b6bcc4;
}

.li-mensagem {
  padding: 10px 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #3d444f;
  background: #f7f9fb;
  border: 1px solid #eef1f4;
  border-radius: 8px;
  overflow-wrap: break-word;
}

/* (16) Cadeia */
.li-cadeia {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.li-elo {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: #fbfcfd;
  border: 1px solid #e6eaef;
  border-radius: 7px;
  cursor: pointer;
  min-width: 0;
}

.li-elo:hover {
  background: #f7f9fb;
}

.li-elo--atual,
.li-elo--atual:hover {
  background: #f2f7fc;
  border-color: #cfe0f2;
  cursor: default;
}

.li-elo__passo {
  font-size: 10px;
  color: #b6bcc4;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.li-elo__meta {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  color: #7a828e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.li-elo__acao {
  font-size: 10.5px;
  font-weight: 700;
  color: #3d7fc4;
  flex-shrink: 0;
}

.li-elo__acao--atual {
  color: #8a929c;
}

/* (17) Payloads */
.li-payloads {
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 12px;
  padding: 14px 18px 18px;
}

.li-payload {
  min-width: 0;
}

.li-payload__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}

.li-payload__nome {
  display: flex;
  align-items: baseline;
  gap: 7px;
  min-width: 0;
}

.li-payload__nome .li-secao__titulo {
  margin-bottom: 0;
}

.li-payload__linhas {
  font-size: 10px;
  color: #b6bcc4;
  white-space: nowrap;
}

.li-payload__acoes {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.li-json {
  height: 280px;
  overflow: auto;
  margin: 0;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e6eaef;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  line-height: 1.55;
  color: #5a636e;
}

.li-json--erro {
  background: #fbf1f1;
  border-color: #f2c9c9;
}

.li-json--full {
  height: auto;
  flex: 1;
  font-size: 13px;
  line-height: 1.65;
  background: #fff;
  border: none;
  border-radius: 0;
  padding: 18px 20px;
}

/* Cores do JSON */
.j-key {
  color: #8a5a1b;
}

.j-str {
  color: #3a7d3a;
}

.j-bool {
  color: #b5651d;
}

.j-num {
  color: #2a6fb0;
}

.j-punc {
  color: #5a636e;
}

.j-nulo {
  color: #9aa2ac;
  font-style: italic;
}

/* ---- Modal tela cheia (18) ---- */
.li-full {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.li-full__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 18px;
  background: #f7f9fb;
  border-bottom: 1px solid #eef1f4;
}

.li-full__titulo {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #2c3441;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.li-full__acoes {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.li-full__busca {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #d4d9df;
  border-radius: 7px;
  color: #9aa2ac;
}

.li-full__busca input {
  width: 190px;
  border: none;
  background: transparent;
  font-size: 12px;
  color: #2c3441;
}

.li-full__busca input:focus {
  outline: none;
}

.li-full__contagem {
  font-size: 10px;
  color: #b6bcc4;
  white-space: nowrap;
}

.li-full__fechar {
  padding: 0 4px;
  font-size: 18px;
  line-height: 1;
  color: #8a929c;
  background: none;
  border: none;
  cursor: pointer;
}

/* ---- Vazios ---- */
.li-empty {
  padding: 40px 20px;
  text-align: center;
  color: #9aa2ac;
  font-size: 13px;
}

.li-empty--detalhe {
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
}

/* ---- Responsividade ---- */
@media (max-width: 1399.98px) {
  .li-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1199.98px) {
  .li-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .li-detail {
    position: static;
  }

  .li-meta {
    flex-wrap: wrap;
  }

  .li-meta__col {
    flex: 1 1 45% !important;
  }

  .li-payloads {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
